import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faMagnifyingGlass, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { getCandidatesForInverterModel, getCandidatesForMagazineModel } from '../../api/compatibility';
import { ProductImage } from '../ProductImage';
import AvailabilityBadge from '../AvailabilityBadge';
import { formatKwota } from '../../utils';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

/**
 * Prawa kolumna sprawdzarki: urządzenia pasujące do wskazanego modelu.
 *
 * ZMIANA PRZEPŁYWU: lista pojawia się OD RAZU po wybraniu modelu, bez klikania
 * „Znajdź kompatybilne". Wcześniej wynik był pod formularzem i wymagał osobnego
 * kliknięcia, więc po wyborze modelu ekran wyglądał, jakby nic się nie stało.
 *
 * KIERUNEK bierzemy z tego, od czego użytkownik zaczyna:
 *   `kierunek='inverter'` (wskazał falownik) -> pokazujemy MAGAZYNY,
 *   `kierunek='battery'`  (wskazał magazyn)  -> pokazujemy FALOWNIKI.
 *
 * ══ FILTRY OPARTE NA TYM, CO ENDPOINT NAPRAWDĘ ZWRACA ══
 * Wiersz kandydata ma: manufacturer, series, capacity_kwh, power_kw, phase,
 * price_min, availability_any. NIE MA pola `voltage` (sprawdzone na obu
 * kierunkach), więc filtra napięcia nie da się zrobić po polu. W praktyce
 * zastępuje go szukajka po nazwie: modele niosą „HV"/„LV" w nazwie, więc
 * wpisanie „HV" zawęża listę do wysokonapięciowych.
 */

// Ile kart widać przed rozwinięciem. Reszta czeka pod przyciskiem, ale W TYM SAMYM
// miejscu — osobna strona zrywałaby kontekst wybranego urządzenia.
const PODGLAD = 3;

/**
 * Jedna klasa przycisku „Sprawdź dostępność" dla OBU stron ekranu. Wcześniej lewy
 * (wybrane urządzenie) był na pełną szerokość, a prawe wąskie — te same słowa
 * wyglądały jak dwie różne akcje o różnej wadze.
 */
export const KLASA_PRZYCISKU = 'inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-lg '
  + 'bg-blue-600 text-white text-xs font-semibold hover:bg-blue-500 transition-colors';

// Etykiety statusów są w słowniku (status_kandydata.*), tu zostają tylko kolory.
const STATUS_OPIS = {
  compatible: { klasa: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  conditional: { klasa: 'bg-amber-50 text-amber-700 border-amber-300' },
  conflicting: { klasa: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const metaLinia = (pokazujemy, r) => (pokazujemy === 'magazyn'
  ? [r.manufacturer, r.capacity_kwh ? `${String(r.capacity_kwh).replace('.', ',')} kWh` : null, r.series ? i18n.t('zgodnosc:seria_meta', { seria: r.series }) : null]
  : [r.manufacturer, r.power_kw ? `${r.power_kw} kW` : null, r.phase ? i18n.t(`zgodnosc:faza.${r.phase}`, { defaultValue: r.phase }) : null]
).filter(Boolean).join(' · ');

/**
 * KLIKALNA JEST CAŁA KARTA, nie sama nazwa i miniatura.
 *
 * Wcześniej klikalne były dwa małe obszary, a reszta kafelka wyglądała tak samo
 * i nie reagowała — trafienie w „martwe" miejsce sprawiało wrażenie, że strona
 * się zacięła. `role="button"` + obsługa Enter/Spacji zamiast <button>, bo
 * w środku jest link „Sprawdź dostępność", a przycisk nie może zawierać linku
 * (zagnieżdżone elementy interaktywne). Link zatrzymuje propagację, więc
 * prowadzi do modelu zamiast otwierać parę.
 */
const KartaKandydata = ({ r, pokazujemy, onWybierz, wybrany }) => {
  const { t } = useTranslation('zgodnosc');
  const st = STATUS_OPIS[r.user_status];
  const wybierz = () => onWybierz?.(r);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={wybierz}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); wybierz(); }
      }}
      aria-label={t('sprawdz_pare_z', { nazwa: r.product_name })}
      className={`rounded-xl border bg-white p-3 cursor-pointer transition-all
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
        wybrany
          ? 'border-blue-500 ring-2 ring-blue-200 shadow-sm'
          : 'border-gray-200 hover:border-blue-300 hover:shadow-sm'
      }`}
    >
      <div className="flex gap-3">
        <div className="w-20 h-20 shrink-0 flex items-center justify-center rounded-lg border border-gray-100 bg-white p-1.5">
          <ProductImage src={r.img_url} productType={pokazujemy} alt={r.product_name} className="max-w-full max-h-full object-contain" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2">{r.product_name}</p>
          <p className="text-xs text-gray-500 mt-0.5">{metaLinia(pokazujemy, r)}</p>
          {st && (
            <span className={`inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded border ${st.klasa}`}>
              {t(`status_kandydata.${r.user_status}`)}
            </span>
          )}
          {/* Warunek zgodności stoi PRZY produkcie, nie w zbiorczej nocie pod listą:
              dotyczy tego jednego modelu i tylko tam ma znaczenie. */}
          {r.short_condition_pl && (
            <p className="text-[11px] text-amber-700 mt-1 leading-4">{r.short_condition_pl}</p>
          )}
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {r.price_min != null && <span className="text-sm font-bold text-blue-600">{t('od_kwoty', { kwota: formatKwota(r.price_min) })}</span>}
            {r.availability_any && <AvailabilityBadge value="Dostępny" />}
          </div>
        </div>
      </div>
      {r.model_url && (
        /* Przycisk wąski i WYŚRODKOWANY. Pełna szerokość robiła z niego najcięższy
           element karty, mocniejszy niż nazwa produktu; wyrównany do lewej zostawiał
           z prawej pustkę. Ta sama klasa co przy wybranym urządzeniu po lewej
           (`vv-btn-dostepnosc`), żeby oba wyglądały identycznie. */
        <div className="mt-2.5 flex justify-center">
          <Link to={r.model_url} onClick={(e) => e.stopPropagation()} className={KLASA_PRZYCISKU}>
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[10px]" />
            {t('sprawdz_dostepnosc')}
          </Link>
        </div>
      )}
    </div>
  );
};

const PanelKompatybilnych = ({ kierunek, modelId, onWybierz, onLiczba, wybranyId }) => {
  const { t } = useTranslation('zgodnosc');
  const [dane, setDane] = useState(null);
  const [ladowanie, setLadowanie] = useState(true);
  const [blad, setBlad] = useState(false);
  const [producent, setProducent] = useState('');
  const [seria, setSeria] = useState('');
  const [szukaj, setSzukaj] = useState('');
  const [rozwiniete, setRozwiniete] = useState(false);

  const pokazujemy = kierunek === 'inverter' ? 'magazyn' : 'inwerter';
  const pobierz = kierunek === 'inverter' ? getCandidatesForInverterModel : getCandidatesForMagazineModel;

  useEffect(() => {
    if (modelId == null) return undefined;
    let anulowane = false;
    setLadowanie(true); setBlad(false);
    // Świeże filtry przy zmianie modelu — inaczej producent wybrany dla poprzedniego
    // urządzenia potrafiłby wyzerować listę i wyglądałoby to jak brak zgodności.
    setProducent(''); setSeria(''); setSzukaj(''); setRozwiniete(false);
    onLiczba?.(null); // w trakcie szukania hero wraca do stanu neutralnego
    pobierz(modelId, { limit: 1200 })
      .then((res) => {
        if (anulowane) return;
        setDane(res); setLadowanie(false);
        const g = res?.groups || {};
        onLiczba?.((g.compatible?.length || 0) + (g.conditional?.length || 0) + (g.conflicting?.length || 0));
      })
      .catch(() => { if (!anulowane) { setBlad(true); setLadowanie(false); onLiczba?.(0); } });
    return () => { anulowane = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kierunek, modelId]);

  const wszystkie = useMemo(() => {
    const g = dane?.groups || {};
    // `unknown` świadomie pomijamy: to cały reszta katalogu, o której nic nie wiemy.
    return [...(g.compatible || []), ...(g.conditional || []), ...(g.conflicting || [])];
  }, [dane]);

  const producenci = useMemo(
    () => [...new Set(wszystkie.map((r) => r.manufacturer).filter(Boolean))].sort(),
    [wszystkie]
  );
  const serie = useMemo(
    () => [...new Set(wszystkie.map((r) => r.series).filter(Boolean))].sort(),
    [wszystkie]
  );

  const widoczne = useMemo(() => {
    const fraza = szukaj.trim().toLowerCase();
    return wszystkie.filter((r) => {
      if (producent && r.manufacturer !== producent) return false;
      if (seria && r.series !== seria) return false;
      if (fraza && !(r.product_name || '').toLowerCase().includes(fraza)) return false;
      return true;
    });
  }, [wszystkie, producent, seria, szukaj]);

  if (modelId == null) return null;

  const naMagazyny = pokazujemy === 'magazyn';
  const selectCls = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500';

  if (ladowanie) {
    return <div className="h-64 rounded-2xl bg-gray-100 animate-pulse" aria-busy="true" aria-label={t('szukanie_zgodnych')} />;
  }
  if (blad) {
    return <p className="text-sm text-rose-500">{t('blad_listy')}</p>;
  }

  if (!wszystkie.length) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/60 px-5 py-8 text-center">
        <p className="text-sm font-semibold text-gray-700 mb-1">{t('brak_danych_tytul')}</p>
        <p className="text-xs text-gray-500 leading-5">
          {t('brak_danych_opis')}
        </p>
      </div>
    );
  }

  const doPokazania = rozwiniete ? widoczne : widoczne.slice(0, PODGLAD);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <h2 className="text-sm font-bold text-gray-900">
          {naMagazyny ? t('pasujace_magazyny') : t('pasujace_falowniki')}
          <span className="ml-1.5 text-xs font-medium text-gray-400">{widoczne.length}</span>
        </h2>
      </div>

      {/* Filtry: producent, seria i szukajka po nazwie. Napięcia NIE ma jako pola
          w odpowiedzi (sprawdzone) — w praktyce zastępuje je szukajka, bo nazwy
          magazynów niosą „HV" albo „LV". */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
        <select value={producent} onChange={(e) => setProducent(e.target.value)} aria-label={t('producent')} className={selectCls}>
          <option value="">{t('wszyscy_producenci')}</option>
          {producenci.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={seria} onChange={(e) => setSeria(e.target.value)} aria-label={t('seria')} className={selectCls}>
          <option value="">{t('dowolna_seria')}</option>
          {serie.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="relative sm:col-span-2">
          <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400" />
          <input
            type="text"
            value={szukaj}
            onChange={(e) => setSzukaj(e.target.value)}
            placeholder={naMagazyny ? t('szukaj_magazynu_przyklad') : t('szukaj_falownika_przyklad')}
            aria-label={t('szukaj_modelu')}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {widoczne.length === 0 ? (
        <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-xl px-4 py-6 text-center">
          {t('zaden_nie_spelnia', { liczba: wszystkie.length })}
        </p>
      ) : (
        <>
          <div className="space-y-2.5">
            {doPokazania.map((r) => (
              <KartaKandydata
                key={r.id} r={r} pokazujemy={pokazujemy} onWybierz={onWybierz}
                wybrany={wybranyId != null && String(r.id) === String(wybranyId)}
              />
            ))}
          </div>

          {/* Rozwinięcie W MIEJSCU, nie na osobnej stronie: wybrane urządzenie zostaje
              na oczach, więc widać, do czego ta lista się odnosi. */}
          {widoczne.length > PODGLAD && (
            <button
              type="button"
              onClick={() => setRozwiniete((v) => !v)}
              className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border
                         border-blue-200 bg-white text-blue-700 text-sm font-semibold hover:bg-blue-50 transition-colors"
            >
              <FontAwesomeIcon icon={faChevronDown} className={`text-xs transition-transform ${rozwiniete ? 'rotate-180' : ''}`} />
              {rozwiniete
                ? t('pokaz_mniej')
                : t(naMagazyny ? 'wszystkie_magazyny' : 'wszystkie_falowniki', { liczba: widoczne.length })}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default PanelKompatybilnych;
